import { Injectable } from '@angular/core';
import * as THREE from 'three';
import { CollisionUtils, Point2D } from 'src/app/utils/math/collision-utils';
import { ConfigurationPlan } from '../../../../../models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { ShapeTypeEnum } from '../../../../../enum/workbench/shape-type.enum';

export { MagnetismIcon } from '../preview-ui-models';

@Injectable({
  providedIn: 'root'
})
export class WorkbenchCollisionService {

  constructor() { }

  /**
   * Calculates the 2D collision polygons for a given object.
   * Handles Rectangle and Angle shapes.
   * @param obj The Three.js Object/Group representing the plan.
   * @param positionOverride Optional position to use instead of the object's current position.
   */
  public getCollisionPolygons(obj: THREE.Object3D, positionOverride?: { x: number, y: number }): Point2D[][] {
    const plan = obj.userData['plan'] as ConfigurationPlan;
    if (!plan) return [];

    const width = plan.width || 600;
    const length = plan.length || 1000;
    const shapeType = plan.shape ? plan.shape.shapeType : ShapeTypeEnum.Rectangle;

    const posX = positionOverride ? positionOverride.x : obj.position.x;
    const posY = positionOverride ? positionOverride.y : obj.position.y;
    const rotation = obj.rotation.z;

    // Helper to rotate a point around 0,0 then translate
    const transform = (x: number, y: number) => {
      const rx = x * Math.cos(rotation) - y * Math.sin(rotation);
      const ry = x * Math.sin(rotation) + y * Math.cos(rotation);
      return { x: rx + posX, y: ry + posY };
    };

    if (shapeType === ShapeTypeEnum.Angle) {
      // Angle Shape (Corner with diagonal cut)
      const L = length;
      const W = width;

      // Centering offset (assuming mesh is centered)
      const offsetX = -L / 2;
      const offsetY = -L / 2;

      const points = [
        { x: 0, y: 0 },
        { x: W, y: 0 },
        { x: L, y: L - W },
        { x: L, y: L },
        { x: 0, y: L }
      ].map(p => transform(p.x + offsetX, p.y + offsetY));

      return [points];
    } else if (shapeType === ShapeTypeEnum.Mushroom) {
      // Mushroom Shape (Stem + Bulb)
      // Requires approximating the arc and handling asymmetry/flipping

      const L = length;
      const W = width;
      // Resolve diameter logic similar to MeshBuilder
      let diam: any = plan.diameter;
      if (!diam && plan.shape?.['diameter']) diam = plan.shape['diameter'];
      if (!diam && (plan as any).shapeDiameter) diam = (plan as any).shapeDiameter;
      const D = Number(diam) || W;
      const R = D / 2;

      // Center Offset is critical because Mesh is centered
      const centerOffset = obj.userData['centerOffset'] || { x: L / 2, y: W / 2 };

      const points: { x: number, y: number }[] = [];
      const CX = L - R; // Center X of the bulb arc

      // Helper to add local point
      const addPoint = (lx: number, ly: number) => {
        // Adjust for Center Offset (Mesh Local Space)
        const mx = lx - centerOffset.x;
        const my = ly - centerOffset.y;
        points.push(transform(mx, my));
      };

      const numArcPoints = 8; // Define here for both branches

      if (plan.isFlip) {
        // FLIPPED: Flush Top (Aligned at Y=W)
        // Bulb Center Y = W - R
        // Stem is from Y=0 to Y=W (Wait, Stem is rectWidth... assuming 0..W)

        const CY = W - R;

        // 1. Top Left
        addPoint(0, W);

        // 2. Top Right (Start of Arc) -> (CX, W)
        // Actually, let's trace the boundary continuously.

        // Start Top-Left
        addPoint(0, W);

        // Top Edge to Arc Start
        // Arc goes from Angle PI/2 down to thetaEnd
        // Start Angle: PI/2 (Top)
        // End Angle: -PI - thetaPrincipal...
        // Let's use the MeshBuilder logic for angles to be precise, or just approximate the semi-circle cap.
        // MeshBuilder: absarc(cx, cy, radius, PI/2, thetaEnd, true) (Clockwise?)
        // True means Clockwise in Three.js absarc?
        // Three.js Shape.absarc(x, y, radius, startAngle, endAngle, clockwise)
        // If clockwise=true, goes PI/2 -> ... -> thetaEnd.

        // We'll generate arc points.
        // We'll generate arc points.
        // Arc Start (Top): (CX, W)
        // Arc End (Bottom-ish): Intersection with Stem or full circle?
        // It connects back to (0,0) via (0, W).
        // Let's just do the full cap for collision simplicity?
        // No, we need the "corner" where stem meets bulb if D > W.
        // But usually D >= W. If D = W, it's a perfect semi-circle.
        // If D > W, it overhangs bottom.

        // Simplification: Generate Arc from PI/2 down to -PI/2 (Right side)
        // Then check connection to Stem.

        // Arc Calculation
        // Top Point is (CX, W) -> Angle PI/2 relative to (CX, CY)
        // Wait, CY = W - R.
        // Y_top = CY + R*sin(PI/2) = (W-R) + R = W. Correct.

        // Bottom Point?
        // If D > W: Bottom is below 0.
        // Intersection with Stem (Y=0)?
        // We need the point where the bulb meets the stem line Y=0?
        // MeshBuilder: lineTo(0,0).
        // So it goes from Arc End to (0,0).
        // Arc End is where?
        // thetaEnd calculation in MeshBuilder:
        // const arg = clamp((radius - rectWidth) / radius); ... thetaEnd = -PI - thetaPrincipal.
        // This seems complex to replicate perfectly.
        // Let's approximate:
        // 1. Trace Top Line: (0, W) -> (CX, W)
        // 2. Trace Arc: From (CX, W) around the right side.
        //    - Angles: PI/2 down to -PI/2 is the right semi-circle.
        //    - If D > W, it continues past -PI/2?
        //    - Or stops before?
        //    - Let's just do the Semi-Circle (PI/2 to -PI/2) + maybe a bit more if needed?
        //    - Actually, snapping to the "Tip" (Angle 0) is most important.
        //    - And snapping to Top/Bottom quadrants.

        // Let's generate points for Angle PI/2 to -PI/2 (Right Cap)
        // relative to (CX, CY).
        for (let i = 0; i <= numArcPoints; i++) {
          const angle = (Math.PI / 2) - (i * Math.PI / numArcPoints); // PI/2 -> -PI/2
          const px = CX + R * Math.cos(angle);
          const py = CY + R * Math.sin(angle);
          addPoint(px, py);
        }

        // 3. Bottom Edge
        // If D > W, last arc point is (CX, W-R-R) = (CX, W-D).
        // If D=W, (CX, 0).
        // We need to connect to (0, 0).
        // If D > W, do we include the "underhang"?
        // Yes, the visual mesh does.
        // But for magnetism, maybe we just connect to (CX, 0) then (0,0)?
        // Visual mesh goes explicitly to (0,0).
        // Let's add (CX, 0) if distinct, then (0,0).
        // Actually, if D > W, (CX, W-D) is below 0.
        // Visual mesh: lineTo(0,0).
        // So we just add (0,0).
        addPoint(0, 0);

        // 4. Close
        // (0, W) is already start.

      } else {
        // STANDARD: Flush Bottom (Aligned at Y=0)
        // Stem is 0..W
        // Bulb Center Y = R.
        // Arc Top is Y = 2R = D.
        // Arc Bottom is Y = 0.

        const CY = R;

        // 1. Bottom Left
        addPoint(0, 0);

        // 2. Bottom Right (Arc Start) -> (CX, 0)
        // Arc goes from -PI/2 (Bottom) up to PI/2 (Top)?
        // MeshBuilder: absarc(cx, cy, radius, -Math.PI / 2, thetaEnd, false) (CCW)
        // Start -PI/2.
        // We'll approximate -PI/2 to PI/2.

        for (let i = 0; i <= numArcPoints; i++) {
          const angle = (-Math.PI / 2) + (i * Math.PI / numArcPoints); // -PI/2 -> PI/2
          const px = CX + R * Math.cos(angle);
          const py = CY + R * Math.sin(angle);
          addPoint(px, py);
        }

        // 3. Top Edge
        // Arc ends at (CX, 2R) = (CX, D).
        // If D > W, this is above W.
        // We need to connect to (0, W).
        addPoint(0, W);

        // 4. Close
        // (0, 0) is start.
      }

      return [points];
    } else {
      // Standard Rectangle (OBB)
      const halfL = length / 2;
      const halfW = width / 2;

      const corners = [
        { x: -halfL, y: -halfW },
        { x: halfL, y: -halfW },
        { x: halfL, y: halfW },
        { x: -halfL, y: halfW }
      ].map(p => transform(p.x, p.y));

      return [corners];
    }
  }

  /**
   * Calculates the Axis Aligned Bounding Box (AABB) from a set of polygons.
   */
  public getBoundsFromPolygons(polygons: Point2D[][]): { left: number, right: number, top: number, bottom: number } {
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    let hasPoints = false;
    // Filter out NaN/Infinity
    polygons.forEach(poly => {
      poly.forEach(p => {
        if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return;

        hasPoints = true;
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      });
    });

    if (!hasPoints) return { left: 0, right: 0, top: 0, bottom: 0 };

    return { left: minX, right: maxX, top: minY, bottom: maxY };
  }

  /**
   * Checks if an object collides with any object in the provided list.
   */
  public checkCollisionWithList(obj: THREE.Object3D, others: THREE.Object3D[]): boolean {
    const polysA = this.getCollisionPolygons(obj);

    for (const other of others) {
      const polysB = this.getCollisionPolygons(other);

      // Check intersection
      for (const polyA of polysA) {
        for (const polyB of polysB) {
          if (CollisionUtils.isPolygonsColliding(polyA, polyB)) {
            return true; // Simplified: SAT intersection
          }
        }
      }
    }
    return false;
  }

  /**
   * Checks if an object is in an invalid collision state (Red).
   */
  public checkInvalidCollision(obj: THREE.Object3D, allObjects: THREE.Object3D[]): boolean {
    const otherShapes = allObjects.filter(c => c !== obj && c.userData['id']);

    // Reduce collision polygon slightly for "Invalid" check to allow touching (snapped) objects
    // This prevents "Red" state when objects are perfectly aligned.
    // We can pass a scale factor or offset.
    // getCollisionPolygons uses full size.
    // Let's implement a shrink helper or just use a generous tolerance in isPolygonsColliding?

    // Standard IS_POLYGONS_COLLIDING uses > tolerance (1.5).
    // If touching, overlap is near 0.
    // So standard check *should* work. 
    // BUT if there is a tiny overlap (e.g. 0.0001), it might trigger if tolerance is small.
    // Wait, CollisionUtils tolerance is 1.5.
    // 0.0001 < 1.5. So it returns FALSE.
    // So for it to return TRUE, overlap must be > 1.5.

    // Maybe the user is snapping to an INTEGRAL coord, but the generic logic logic led to > 1.5 overlap?
    // Let's debug by logging? No, I can't.
    // I will increase the tolerance passed to `isPolygonsColliding` implicitly by checking logic.
    // Actually, `checkCollisionWithList` calls `isPolygonsColliding` with default.
    // I will create a specific check here that is more permissive.

    const polysA = this.getCollisionPolygons(obj);

    for (const other of otherShapes) {
      const polysB = this.getCollisionPolygons(other);
      for (const polyA of polysA) {
        for (const polyB of polysB) {
          // Use a higher tolerance for declaring "Invalid Visual State"
          // e.g. 2.0 or 3.0 to safely ignore floating point noise at edges
          if (CollisionUtils.isPolygonsColliding(polyA, polyB, 3)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * Finds a valid position for an object that is colliding, by moving it to the right.
   */
  public findSmartPosition(obj: THREE.Object3D, others: THREE.Object3D[]): THREE.Vector3 {
    let candidatePos = obj.position.clone();
    const margin = 200; // 20cm
    const maxRowWidth = 4000; // Wrap to new row after 4m
    const rowHeightStep = 1500; // 1.5m vertical step 

    let attempts = 0;

    // We try to place it. If collision, we move right. 
    // If Right > maxRowWidth, we reset X and move Y down.

    // Naive iterative approach:
    // 1. Start at current candidate (usually 0,0)
    // 2. Check collision.
    // 3. If collide, move right.
    // 4. If X > max, move to next row (reset X, add Y).
    // 5. Repeat.

    // Better: Get all bounds, find first gap?
    // Let's stick to the incremental "Push Right" approach but add Wrapping.

    let collisionFound = true;
    while (collisionFound && attempts < 100) {
      collisionFound = false;
      const objBounds = this.getBoundsFromPolygons(this.getCollisionPolygons(obj, candidatePos));

      // Check against all others
      for (const other of others) {
        other.updateMatrixWorld(true);
        const otherBounds = this.getBoundsFromPolygons(this.getCollisionPolygons(other));

        // Interaction Check (AABB)
        const overlapX = Math.min(objBounds.right, otherBounds.right) - Math.max(objBounds.left, otherBounds.left);
        const overlapY = Math.min(objBounds.bottom, otherBounds.bottom) - Math.max(objBounds.top, otherBounds.top);

        if (overlapX > 1 && overlapY > 1) {
          collisionFound = true;

          // Move Right
          const leftOffset = candidatePos.x - objBounds.left;
          candidatePos.x = otherBounds.right + margin + leftOffset;

          // Wrap Logic
          if (candidatePos.x > maxRowWidth) {
            // Reset X to start (approx 0 or bounding box left?)
            // Let's say 0 for simplicity, or keep current X but move Y.
            // Actually, to Wrap, we need to know the "Line Height".
            // Simple wrap: 
            candidatePos.x = 0;
            candidatePos.y -= rowHeightStep; // Move down
          }

          break; // Check again with new position
        }
      }
      attempts++;
    }

    // If still colliding after 100 attempts, just return original to avoid infinity
    if (collisionFound) {
      console.warn('findSmartPosition could not find a free spot, returning original.');
      return obj.position.clone();
    }

    return candidatePos;
  }

  public isCornerTouchingOtherPlans(point: THREE.Vector3, currentPlan: ConfigurationPlan, allObjects: THREE.Object3D[]): boolean {
    const others = allObjects.filter(c => c.userData['plan'] && c.userData['plan'] !== currentPlan);

    for (const other of others) {
      const bounds = this.getBoundsFromPolygons(this.getCollisionPolygons(other));
      const TOL = 1;

      const onLeft = Math.abs(point.x - bounds.left) < TOL;
      const onRight = Math.abs(point.x - bounds.right) < TOL;
      const onTop = Math.abs(point.y - bounds.top) < TOL;
      const onBottom = Math.abs(point.y - bounds.bottom) < TOL;

      const withinX = point.x >= bounds.left - TOL && point.x <= bounds.right + TOL;
      const withinY = point.y >= bounds.top - TOL && point.y <= bounds.bottom + TOL;

      if ((onLeft || onRight) && withinY) return true;
      if ((onTop || onBottom) && withinX) return true;
    }
    return false;
  }

  public isEdgeTouchingOtherPlans(midPoint: THREE.Vector3, currentPlan: ConfigurationPlan, allObjects: THREE.Object3D[]): boolean {
    const TOLERANCE = 10;

    // Note: This logic seems specific to using Mesh.worldToLocal to check proximity.
    // If we only have Groups/Objects in service, we might need to rely on bounding boxes or polygons.
    // However, the original code used scene.children which are Groups (with Plans).
    // Let's adapt to use Polygons/Bounds which is safer in service (no reliance on Scene structure/Mesh presence).

    const candidates = allObjects.filter(c => c.userData['plan'] && c.userData['plan'] !== currentPlan);

    for (const other of candidates) {
      // Use Global Check since we have world midpoint
      const bounds = this.getBoundsFromPolygons(this.getCollisionPolygons(other));
      const p = midPoint; // World Point

      // Expanded bounds check
      if (p.x >= bounds.left - TOLERANCE && p.x <= bounds.right + TOLERANCE &&
        p.y >= bounds.top - TOLERANCE && p.y <= bounds.bottom + TOLERANCE) {
        return true;
      }
    }
    return false;
  }

  /**
   * Refactored logic to detect assembled groups.
   * Returns list of lists of IDs.
   */
  public findGroups(allObjects: THREE.Object3D[]): number[][] {
    const visited = new Set<THREE.Object3D>();
    const groups: number[][] = [];
    const tolerance = 5;
    const expandVec = new THREE.Vector3(tolerance, tolerance, 50);

    for (const obj of allObjects) {
      if (visited.has(obj)) continue;

      const currentGroup: number[] = [];
      const stack = [obj];
      // Keep track of visited in this traversal to avoid cycles if graph is weird (though set handles it)

      while (stack.length > 0) {
        const current = stack.pop()!;
        if (visited.has(current)) continue;
        visited.add(current);
        currentGroup.push(current.userData['id']);

        const currentBox = new THREE.Box3().setFromObject(current);
        currentBox.expandByVector(expandVec);

        for (const other of allObjects) {
          if (!visited.has(other)) {
            const otherBox = new THREE.Box3().setFromObject(other);
            if (currentBox.intersectsBox(otherBox)) {
              stack.push(other);
            }
          }
        }
      }
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }
    }
    return groups;
  }
  /**
 * Checks for magnetism (snapping) between the object being moved and other objects in the scene.
 * @param obj The object being moved.
 * @param proposedPos The proposed position of the object (from drag).
 * @param scene The scene containing all objects.
 * @param cameraZoom Current camera zoom level (to calculate pixel threshold).
 * @param containerHeight Container height (to calculate pixel threshold).
 * @returns The snapped position and any magnetism icons to display.
 */
  public checkMagnetism(
    obj: THREE.Object3D,
    proposedPos: THREE.Vector3,
    scene: THREE.Scene,
    visibleWorldHeight: number,
    containerHeight: number
  ): { snapped: boolean, position: THREE.Vector3, snapPoint?: THREE.Vector3 } {

    // Dynamic Snap Threshold logic
    const SNAP_PIXELS = 30; // Increased to 30 to give a "hard stop" feel
    // World Units per Pixel
    const worldPerPixel = visibleWorldHeight / containerHeight;
    const safeWorldPerPixel = (worldPerPixel > 0 && Number.isFinite(worldPerPixel)) ? worldPerPixel : 2.5;
    const SNAP_THRESHOLD = SNAP_PIXELS * safeWorldPerPixel;

    let bestSnapPos = proposedPos.clone();
    let minSnapDist = SNAP_THRESHOLD; // Only snap if closer than this
    let hasSnapped = false;
    let snapIconPos: THREE.Vector3 | null = null;

    // Disable magnetism for Circle shapes for now (or handle center-snap later)
    if (obj.userData['plan']?.shape?.shapeType === ShapeTypeEnum.Circle) {
      return { snapped: false, position: proposedPos };
    }

    const objPolys = this.getCollisionPolygons(obj, { x: proposedPos.x, y: proposedPos.y });
    // Flatten obj points for easier iteration
    const objPoints = objPolys.flat();

    const otherShapes = scene.children.filter(c => c !== obj && c.userData['id'] && c.visible);

    for (const other of otherShapes) {
      if (other.userData['plan']?.shape?.shapeType === ShapeTypeEnum.Circle) {
        continue;
      }

      const otherPolys = this.getCollisionPolygons(other);
      const otherPoints = otherPolys.flat();

      // --- 1. Vertex-to-Vertex Snapping (Corner to Corner) ---
      // This is the highest priority for "assembly".
      for (const pA of objPoints) {
        for (const pB of otherPoints) {
          const dist = Math.sqrt(Math.pow(pA.x - pB.x, 2) + Math.pow(pA.y - pB.y, 2));

          if (dist < minSnapDist) {
            minSnapDist = dist;
            // Snapping A to B
            const deltaX = pB.x - pA.x;
            const deltaY = pB.y - pA.y;

            bestSnapPos.set(proposedPos.x + deltaX, proposedPos.y + deltaY, proposedPos.z);
            hasSnapped = true;
            snapIconPos = new THREE.Vector3(pB.x, pB.y, 0);
          }
        }
      }

      // --- 2. Vertex-to-Edge Snapping (Sliding / T-Junctions) ---
      // Only check if no corner snap found yet (Priority: Corner > Edge)
      if (!hasSnapped) {

        // Helper: Distance from Point P to Segment AB
        const distToSegment = (p: Point2D, a: Point2D, b: Point2D): { dist: number, closest: Point2D } | null => {
          const l2 = Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2);
          if (l2 === 0) return { dist: Math.sqrt(Math.pow(p.x - a.x, 2) + Math.pow(p.y - a.y, 2)), closest: a };

          let t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / l2;
          t = Math.max(0, Math.min(1, t)); // Clamp to segment

          const proj = { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) };
          const d = Math.sqrt(Math.pow(p.x - proj.x, 2) + Math.pow(p.y - proj.y, 2));
          return { dist: d, closest: proj };
        };

        // Check A: Obj Vertices -> Other Edges
        for (const pA of objPoints) {
          for (let i = 0; i < otherPoints.length; i++) {
            const p1 = otherPoints[i];
            const p2 = otherPoints[(i + 1) % otherPoints.length];

            const result = distToSegment(pA, p1, p2);
            if (result && result.dist < minSnapDist) {
              minSnapDist = result.dist;
              const deltaX = result.closest.x - pA.x;
              const deltaY = result.closest.y - pA.y;

              bestSnapPos.set(proposedPos.x + deltaX, proposedPos.y + deltaY, proposedPos.z);
              // Don't set hasSnapped = true yet, allow searching for better edge snaps globally? 
              // Actually, local optimal is fine.
              // But we need to track if we found a candidate.
              // Let's use a temp flag or just update bestSnapPos and assume we'll use it.
              snapIconPos = new THREE.Vector3(result.closest.x, result.closest.y, 0);
            }
          }
        }

        // Check B: Other Vertices -> Obj Edges
        for (const pB of otherPoints) {
          for (let i = 0; i < objPoints.length; i++) {
            const p1 = objPoints[i];
            const p2 = objPoints[(i + 1) % objPoints.length];

            const result = distToSegment(pB, p1, p2);
            if (result && result.dist < minSnapDist) {
              minSnapDist = result.dist;
              // Move Obj Edge to match Point B
              // Vector is pB - ClosestOnEdge
              // We move Obj by that vector
              const deltaX = pB.x - result.closest.x;
              const deltaY = pB.y - result.closest.y;

              bestSnapPos.set(proposedPos.x + deltaX, proposedPos.y + deltaY, proposedPos.z);
              snapIconPos = new THREE.Vector3(pB.x, pB.y, 0);
            }
          }
        }
      }
    }

    if (minSnapDist < SNAP_THRESHOLD) {
      hasSnapped = true;
    }

    return { snapped: hasSnapped, position: bestSnapPos, snapPoint: snapIconPos || undefined };
  }
}
